import React, { useEffect, useState } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Seo, { SITE } from '../../components/Seo/Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faArrowLeft,
	faLocationDot,
	faCalendarDay,
	faClock,
	faChildReaching,
	faUserGroup,
	faStar,
	faCheck,
	faChevronDown,
	faCircleCheck,
	faArrowRotateLeft,
	faCircleQuestion,
	faCircleInfo,
	faGear,
} from '@fortawesome/pro-regular-svg-icons';

import {
	fetchWorkshopBySlug,
	workshopSeatsRemaining,
} from '../../utils/workshopService';
import { isDevPreview } from '../../utils/devPreview';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { firePendingPurchase, clearPendingPurchase } from '../../utils/metaPixel';
import { getInstructors } from '../../data/instructors';
import { workshopFaqs } from '../../data/faqs';
import RegistrationForm from './RegistrationForm';

// Watercolor washes for the themed hero (imported so webpack resolves the URLs).
import washBlue from '../../assets/waterColorBlue.png';
import washOrange from '../../assets/waterColorOrange.png';
import washPink from '../../assets/waterColorPrimaryPink.png';
import washLightPink from '../../assets/waterColorLightPink.png';

import './Summer.css'; // RegistrationForm/WaitlistForm classes (summer-form-*)
import './SummerLanding.css'; // shared .s-pill / .s-policy
import './SummerDetail.css';

const MONTHS = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ABBR = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Per-workshop theme (matches the Surface-1 landing card accents + the mockup).
const THEME = {
	songwriting: { grad: 'var(--grad-custom)', ink: '#993556', wash: washPink, tintBg: '#fbeaf0', tintFg: '#993556', category: 'Songwriting' },
	allstate: { grad: 'var(--grad-summer)', ink: '#2f7d77', wash: washBlue, tintBg: '#e1f2ef', tintFg: '#2f7d77', category: 'All-State prep' },
	acting: { grad: 'var(--grad-lessons)', ink: '#b35a2c', wash: washOrange, tintBg: '#fbe4d8', tintFg: '#b35a2c', category: 'Acting' },
	mtaudition: { grad: 'var(--grad-littles)', ink: '#8a5a14', wash: washLightPink, tintBg: '#fbf0d8', tintFg: '#8a5a14', category: 'MT audition' },
};

// "9:00 AM" → "9am"; "12:30 PM" → "12:30pm".
function shortTime(t) {
	if (!t) return '';
	const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(t).trim());
	if (!m) return t;
	const [, h, mm, ap] = m;
	return mm === '00' ? `${h}${ap.toLowerCase()}` : `${h}:${mm}${ap.toLowerCase()}`;
}
const timeSpan = (g) => (g?.startTime && g?.endTime ? `${shortTime(g.startTime)}–${shortTime(g.endTime)}` : 'Time TBD');
const shortMonth = (iso) => (iso ? MONTH_ABBR[Number(String(iso).split('-')[1]) - 1] || '' : '');
const monthName = (iso) => (iso ? MONTHS[Number(String(iso).split('-')[1]) - 1] || '' : '');
const dayOf = (iso) => Number(String(iso).split('-')[2]);
// Weekday range from a dateLabel like "June 15–18, 2026 (Mon–Thu)" → "Mon–Thu".
const weekdays = (dateLabel) => {
	const m = /\(([^)]+)\)/.exec(dateLabel || '');
	return m ? m[1] : '';
};
// "Jun 15–18" (same month) or "Jun 29–Jul 2" (cross-month).
function shortDateRange(w) {
	if (!w.startDate) return '';
	const sm = shortMonth(w.startDate);
	const sd = dayOf(w.startDate);
	if (!w.endDate || w.startDate === w.endDate) return `${sm} ${sd}`;
	const em = shortMonth(w.endDate);
	const ed = dayOf(w.endDate);
	return sm === em ? `${sm} ${sd}–${ed}` : `${sm} ${sd}–${em} ${ed}`;
}

// Status pill driven by REAL seat counts (mirrors the landing-card logic).
function seatPill(w) {
	if (w.status === 'draft') return { tone: 'soon', label: 'Draft (preview)' };
	if (w.status === 'closed') return { tone: 'closed', label: 'Closed' };
	const seats = workshopSeatsRemaining(w);
	if (seats <= 0) return { tone: 'full', label: 'Full · join waitlist' };
	if (seats <= 5) return { tone: 'low', label: `Nearly full · ${seats} ${seats === 1 ? 'spot' : 'spots'}` };
	return { tone: 'open', label: 'Open · spots available' };
}

const WorkshopDetail = () => {
	const { workshopId } = useParams();
	const { userProfile } = useAuth();
	const isAdminUser = userProfile?.role === 'admin';
	const [searchParams] = useSearchParams();
	const previewParam = searchParams.get('preview') === 'true';
	// Locally (isDevPreview) draft detail pages are reachable without ?preview=true,
	// mirroring the list/fetch layer (workshopService). In production isDevPreview()
	// is false, so drafts stay hidden from the public exactly as before.
	// Admin-only draft preview (Review 3 #1); isDevPreview keeps local dev working.
	const includeDrafts = (previewParam && isAdminUser) || isDevPreview();
	const previewSuffix = previewParam ? '?preview=true' : '';
	const status = searchParams.get('status');

	const [workshop, setWorkshop] = useState(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);

	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const data = await fetchWorkshopBySlug(workshopId);
				if (!active) return;
				// Hide drafts from the public unless in preview mode.
				if (!data || (data.status === 'draft' && !includeDrafts)) {
					setNotFound(true);
				} else {
					setWorkshop(data);
				}
			} catch (err) {
				console.error('Failed to load workshop:', err);
				if (active) setNotFound(true);
			} finally {
				if (active) setLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, [workshopId, includeDrafts]);

	// On the Stripe checkout success surface, fire the Meta Pixel Purchase event
	// from the amount stashed before the redirect. firePendingPurchase clears the
	// stash, so a reload or bookmarked ?status=success can't double-count.
	useEffect(() => {
		if (status === 'success') firePendingPurchase();
		// Cancelled checkout: disarm the stash so a later history-nav to a stale
		// ?status=success URL can't fire a bogus Purchase (Review 3 #37).
		else if (status === 'cancelled') clearPendingPurchase();
	}, [status]);

	const scrollToRegister = () => {
		document
			.getElementById('register')
			?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	if (loading) {
		return (
			<main className='cyv summer-page'>
				<div className='summer-loading'>
					<Spinner animation='border' style={{ color: 'var(--cyv-teal-600)' }}>
						<span className='visually-hidden'>Loading…</span>
					</Spinner>
				</div>
			</main>
		);
	}

	if (notFound) {
		return (
			<main className='cyv summer-page'>
				<h1 className='brand-heading'>Workshop not found</h1>
				<p className='summer-intro'>
					We couldn't find that workshop. It may not be open for registration yet.
				</p>
				<Link to={`/summer${previewSuffix}`} className='btn-cyv-teal'>
					Back to all workshops
				</Link>
			</main>
		);
	}

	const w = workshop;
	const theme = THEME[w.heroTheme] || THEME.acting;
	const themeVars = {
		'--dt-grad': theme.grad,
		'--dt-ink': theme.ink,
		'--dt-wash': `url(${theme.wash})`,
		'--dt-tint-bg': theme.tintBg,
		'--dt-tint-fg': theme.tintFg,
	};

	const groups = w.groups || [];
	const multiGroup = !!w.requiresGroupSelection && groups.length > 1;
	const pill = seatPill(w);
	const isFull = pill.tone === 'full';
	const isClosed = w.status === 'closed';

	// ---- Quick facts (from live data) ----
	const ageRanges = [...new Set(groups.map((g) => g.ageRange).filter(Boolean))];
	const caps = groups.map((g) => g.capacity).filter(Boolean);
	const capValue = caps.length ? `Cap ${Math.max(...caps)}` : '—';
	const multiDay = w.startDate && w.endDate && w.startDate !== w.endDate;

	// Time fact: single span (+ "daily") or one entry per group.
	const timeValue = multiGroup
		? groups.map((g) => timeSpan(g)).join(' · ')
		: timeSpan(groups[0]);
	const timeSmall = multiGroup
		? ageRanges.length ? 'two age groups' : `${groups.length} groups`
		: multiDay ? 'daily' : null;

	// "For/Ages" fact: real age ranges if the data has them, else a short
	// audience label drawn from the workshop's stated focus (no fabricated ages).
	const audienceFallback = { allstate: 'Entering grades 9–12', mtaudition: 'MT auditioners' };

	// Register-aside list lines.
	const datePretty = (w.dateLabel || '')
		.replace(/,\s*\d{4}/, '')
		.replace(/\s*\(([^)]+)\)/, ' · $1');
	const timeLine = multiGroup
		? groups.map((g) => `${g.label} ${timeSpan(g)}`).join(' · ')
		: `${timeSpan(groups[0])}${multiDay ? ' daily' : ''}`;

	// Instructors. All-State (Annelise leads) uses the stacked-avatars panel
	// pattern; everyone else renders the ordered instructor cards as-is (the
	// catalog already orders them Grace-first except where Annelise leads).
	const isPanelLayout = w.heroTheme === 'allstate';
	let leadCards = getInstructors(w.instructors, workshopId);
	let panel = null;
	if (isPanelLayout && leadCards.length > 1) {
		const lead = leadCards[0];
		// Panel = the rest, reordered Grace-first.
		const rest = leadCards
			.slice(1)
			.sort((a, b) => (a.name === 'Grace Wall' ? -1 : b.name === 'Grace Wall' ? 1 : 0));
		leadCards = [lead];
		panel = rest;
	}
	const soloPeople = !isPanelLayout && leadCards.length <= 1;

	const fineText = multiGroup
		? 'Pick your group at checkout. Full refund 14+ days before start, or within 48 hrs of registering — whichever is later.'
		: 'Secure checkout. Full refund 14+ days before start, or within 48 hrs of registering — whichever is later.';

	const ctaLabel = isFull ? 'Join the waitlist' : 'Register now';

	return (
		<main className='cyv dt' style={themeVars}>
			<Seo
				title={`${w.title} | Color Your Voice Summer Workshops`}
				description={w.summary}
				canonical={`${SITE}/summer/${workshopId}`}
			/>

			{/* ---- Hero ---- */}
			<header className='dt-hero'>
				<span className='dt-hero__bar' />
				<span className='dt-hero__wash' />
				<span className='dt-hero__scrim' />
				<div className='dt-hero__in'>
					<Link to={`/summer${previewSuffix}`} className='dt-back'>
						<FontAwesomeIcon icon={faArrowLeft} /> All summer workshops
					</Link>
					<span className='eyebrow dt-eyebrow'>
						Summer 2026 · {theme.category} · {monthName(w.startDate)}
					</span>
					<h1 className='brand-heading'>{w.title}</h1>
					<p className='dt-hero__lead'>{w.summary}</p>
					<div className='dt-hero__cta'>
						<button type='button' className='btn-cyv-teal' onClick={scrollToRegister}>
							{isClosed || isFull ? 'Join the waitlist' : `Register — ${w.priceLabel}`}
						</button>
						<button type='button' className='dt-hero__ask' onClick={scrollToRegister}>
							Have a question?
						</button>
					</div>
					<span className='dt-loc'>
						<FontAwesomeIcon icon={faLocationDot} /> Inside {w.location?.name}, Burnsville
					</span>
				</div>
			</header>

			{/* ---- Update notice (e.g. reschedule callout), near the top ---- */}
			{w.updateNotice && (
				<div className='dt-banner'>
					<div className='dt-notice' role='status'>
						<FontAwesomeIcon icon={faCircleInfo} className='dt-notice__ic' />
						<p>{w.updateNotice}</p>
					</div>
				</div>
			)}

			{/* ---- Post-checkout status banner ---- */}
			{(status === 'success' || status === 'cancelled') && (
				<div className='dt-banner'>
					{status === 'success' ? (
						<Alert variant='success' className='summer-status-alert mb-0'>
							<FontAwesomeIcon icon={faCircleCheck} className='me-2' />
							Thank you for registering! A confirmation email is on its way. We
							can't wait to see you this summer.
						</Alert>
					) : (
						<Alert variant='warning' className='summer-status-alert mb-0'>
							Your registration wasn't completed and you have not been charged.
							You're welcome to try again whenever you're ready.
						</Alert>
					)}
				</div>
			)}

			{/* ---- Quick-facts strip ---- */}
			<div className='dt-facts'>
				<div className='dt-facts__in'>
					<div className='dt-fact'>
						<div className='dt-fact__k'><FontAwesomeIcon icon={faCalendarDay} /> Dates</div>
						<div className='dt-fact__v'>
							{/* `datesFact` is an explicit override for non-contiguous
							    schedules (e.g. Songwriting: Mon + Thu–Fri), where the
							    computed contiguous range would read wrong. */}
							{w.datesFact ? (
								w.datesFact
							) : (
								<>
									{shortDateRange(w)}
									{weekdays(w.dateLabel) && <small>{weekdays(w.dateLabel)}</small>}
								</>
							)}
						</div>
					</div>
					<div className='dt-fact'>
						<div className='dt-fact__k'><FontAwesomeIcon icon={faClock} /> Time</div>
						<div className='dt-fact__v'>
							{timeValue}
							{timeSmall && <small>{timeSmall}</small>}
						</div>
					</div>
					<div className='dt-fact'>
						<div className='dt-fact__k'>
							<FontAwesomeIcon icon={ageRanges.length ? faChildReaching : faStar} />{' '}
							{ageRanges.length ? 'Ages' : 'For'}
						</div>
						<div className='dt-fact__v'>
							{ageRanges.length
								? ageRanges.join(' & ')
								: audienceFallback[w.heroTheme] || 'All ages'}
						</div>
					</div>
					<div className='dt-fact'>
						<div className='dt-fact__k'><FontAwesomeIcon icon={faUserGroup} /> Group size</div>
						<div className='dt-fact__v'>
							{capValue}
							{groups.length > 1 && <small>per group</small>}
						</div>
					</div>
					<div className='dt-fact'>
						<div className='dt-fact__k'><FontAwesomeIcon icon={faLocationDot} /> Location</div>
						<div className='dt-fact__v'>
							Burnsville<small>{w.location?.name}</small>
						</div>
					</div>
				</div>
			</div>

			{/* ---- Body grid ---- */}
			<div className='dt-body'>
				<main className='dt-main'>
					{/* What you'll do */}
					<section className='dt-sec'>
						<h2>What you'll do</h2>
						<p>{w.description}</p>
						{Array.isArray(w.highlights) && w.highlights.length > 0 && (
							<ul className='dt-skills'>
								{w.highlights.map((h, i) => (
									<li key={i}>
										<FontAwesomeIcon icon={faCheck} />
										<span>{h}</span>
									</li>
								))}
							</ul>
						)}
					</section>

					{/* Day-by-day (only when the catalog has a schedule) */}
					{Array.isArray(w.schedule) && w.schedule.length > 0 && (
						<section className='dt-sec'>
							<h2>What the week looks like</h2>
							<div
								className='dt-days'
								style={{ '--dt-day-cols': Math.min(w.schedule.length, 4) }}>
								{w.schedule.map((s, i) => {
									// Labels look like "Days 1–3 · June 22–24" → split phase / date.
									const [phase, ...rest] = String(s.label || '').split(' · ');
									const datePart = rest.join(' · ');
									return (
										<div key={i} className='dt-day'>
											<span className='dt-day__n'>
												<FontAwesomeIcon icon={faCalendarDay} />
											</span>
											{datePart && <div className='dt-day__d'>{datePart}</div>}
											<h3 className='dt-day__t'>{phase}</h3>
											<p className='dt-day__b'>{s.detail}</p>
										</div>
									);
								})}
							</div>
							{w.scheduleNote && (
								<p className='dt-note'>{w.scheduleNote}</p>
							)}
						</section>
					)}

					{/* Who you'll work with */}
					{(leadCards.length > 0 || panel) && (
						<section className='dt-sec'>
							<h2>
								{leadCards.length + (panel ? panel.length : 0) > 1
									? "Who you'll work with"
									: 'Your teaching artist'}
							</h2>
							<div className={`dt-people${soloPeople ? ' dt-people--solo' : ''}`}>
								{leadCards.map((ins) => (
									<article key={ins.name} className='dt-person'>
										<img src={ins.photo} alt={`Headshot of ${ins.name}`} />
										<div className='dt-person__head'>
											<h4>{ins.name}</h4>
											<div className='dt-person__role'>
												{ins.role}
												{isPanelLayout && ' · Lead'}
											</div>
										</div>
										<div className='dt-person__bios'>
											<p className='dt-person__bio'>{ins.bio}</p>
										</div>
									</article>
								))}
								{panel && (
									<article className='dt-person'>
										<div className='dt-person__avatars'>
											{panel.map((p) => (
												<img key={p.name} src={p.photo} alt={`Headshot of ${p.name}`} />
											))}
										</div>
										<div className='dt-person__head'>
											<h4>Your audition panel</h4>
											<div className='dt-person__role'>
												{panel.map((p) => p.name).join(' & ')}
											</div>
										</div>
										<div className='dt-person__bios'>
											{panel.map((p) => (
												<p key={p.name} className='dt-person__bio'>
													<b>{p.name.split(' ')[0]}:</b> {p.bio}
												</p>
											))}
										</div>
									</article>
								)}
							</div>
							{w.instructorNote && <p className='dt-note'>{w.instructorNote}</p>}
						</section>
					)}

					{/* Good to know (FAQ) */}
					<section className='dt-sec dt-faq' id='faq'>
						<h2>Good to know</h2>
						{workshopFaqs.map((f, i) => (
							<details key={i} open={i === 0}>
								<summary>
									{f.q} <FontAwesomeIcon icon={faChevronDown} />
								</summary>
								<div className='dt-faq__a'>{f.a}</div>
							</details>
						))}
					</section>

					{/* Preview-only TODO notes (dev/staging) */}
					{isAdminUser && w.notes && (
						<p className='dt-note'>
							<FontAwesomeIcon icon={faGear} className='me-2' />
							{w.notes}
						</p>
					)}
				</main>

				{/* Sticky register aside (desktop) */}
				<aside className='dt-aside'>
					<div className='dt-reg'>
						<span className='dt-reg__bar' />
						<div className='dt-reg__price'>
							{w.priceLabel} <small>/ student</small>
						</div>
						<span className={`dt-reg__status s-pill s-pill--${pill.tone}`}>
							<span className='s-pill__dot' />
							{pill.label}
						</span>
						<ul className='dt-reg__list'>
							<li>
								<FontAwesomeIcon icon={faCalendarDay} />
								<span>{datePretty}</span>
							</li>
							<li>
								<FontAwesomeIcon icon={faClock} />
								<span>{timeLine}</span>
							</li>
							<li>
								<FontAwesomeIcon icon={faLocationDot} />
								<span>{w.location?.name}, Burnsville</span>
							</li>
						</ul>
						{isClosed ? (
							<>
								<button
									type='button'
									className='btn-cyv-teal dt-reg__cta'
									disabled
									aria-disabled='true'>
									Enrollment closed
								</button>
								<button type='button' className='dt-reg__waitlink' onClick={scrollToRegister}>
									Join the waitlist
								</button>
							</>
						) : (
							<button
								type='button'
								className='btn-cyv-teal dt-reg__cta'
								onClick={scrollToRegister}>
								{ctaLabel}
							</button>
						)}
						<p className='dt-reg__fine'>{fineText}</p>
					</div>
				</aside>
			</div>

			{/* ---- Register / checkout — the real flow, full-width .co- band ---- */}
			<section className='dt-checkout' id='register'>
				<RegistrationForm workshop={w} />
			</section>

			{/* ---- Policy strip (page content; site footer renders below) ---- */}
			<footer className='s-policy'>
				<div className='s-policy__in'>
					<div className='s-policy__item'>
						<FontAwesomeIcon icon={faCircleCheck} />
						<div>
							<h5>Register online</h5>
							<p>
								Workshops register right here — self-serve and secure. Spots
								confirm once payment is complete.
							</p>
						</div>
					</div>
					<div className='s-policy__item'>
						<FontAwesomeIcon icon={faArrowRotateLeft} />
						<div>
							<h5>Flexible refunds</h5>
							<p>
								Full refund 14+ days before start, or within 48 hours of
								registering — whichever is later. Full refund if we cancel.
							</p>
						</div>
					</div>
					<div className='s-policy__item'>
						<FontAwesomeIcon icon={faCircleQuestion} />
						<div>
							<h5>Questions first?</h5>
							<p>
								Email sing@coloryourvoice.com or call (952) 283-0560 — we'll help
								you find the right fit.
							</p>
						</div>
					</div>
				</div>
			</footer>

			{/* Sticky mobile register bar (<600px) */}
			<div className='dt-mobilebar'>
				<span className='price'>
					{w.priceLabel} <small>/ student</small>
				</span>
				<button
					type='button'
					className='btn-cyv-teal dt-mobilebar__cta'
					onClick={scrollToRegister}>
					{isClosed || isFull ? 'Waitlist' : 'Register'}
				</button>
			</div>
		</main>
	);
};

export default WorkshopDetail;
